// Power meters (docs/device-protocol.md §14–15): a meter's settings (the LAN host a gateway polls, and the compressor
// thresholds: learned, set by hand, or forgotten to be learned again), and a Shelly connected without a gateway (its
// enrollment, and a new connection address). The address holds the device's token: it is shown once.

import type { EnergyMeter } from '@cck/schema';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { useCurrentOrg } from '../components/Shell.tsx';
import { Alert, Button, Field, Input, Modal, Select } from '../components/ui.tsx';
import { useI18n } from '../i18n/index.ts';
import { ApiError } from '../lib/api.ts';
import { formatDateTime, formatNumber } from '../lib/format.ts';
import { orgApi, refreshScoped, useEnergySettings, useSites } from '../lib/queries.ts';

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const fields = Object.values(err.fieldErrors());
    return fields.length ? `${err.message} (${fields.join(', ')})` : err.message;
  }
  return (err as Error).message;
}

const dec = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

// ---------------------------------------------------------------- a meter's settings

export function MeterDialog({ meter, onClose }: { meter: EnergyMeter; onClose: () => void }) {
  const { orgId } = useCurrentOrg();
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const th = meter.thresholds;
  const [host, setHost] = useState(meter.host ?? '');
  const [form, setForm] = useState({ on_w: th ? String(th.on_w) : '', off_w: th?.off_w != null ? String(th.off_w) : '',
    defrost_min_w: th?.defrost_min_w != null ? String(th.defrost_min_w) : '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await orgApi(orgId).updateMeter(meter.probe_id, body);
      await refreshScoped(qc, orgId, ['energy', 'device', 'probes']);
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = {};
    if (!meter.direct && host.trim() !== (meter.host ?? '')) body.host = host.trim() || null;
    const on = dec(form.on_w);
    const changed = on !== (th?.on_w ?? null) || dec(form.off_w) !== (th?.off_w ?? null) || dec(form.defrost_min_w) !== (th?.defrost_min_w ?? null);
    if (changed && on !== null) {
      body.thresholds = { on_w: on, off_w: dec(form.off_w), defrost_min_w: dec(form.defrost_min_w) };
    }
    if (Object.keys(body).length === 0) return onClose();
    void run(body);
  };

  return (
    <Modal open wide title={t('meters.title', { meter: meter.label ?? meter.channel })} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        {error && <Alert tone="red">{error}</Alert>}
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm sm:grid-cols-4">
          <dt className="text-slate-500">{t('meters.model')}</dt><dd>{meter.model ?? meter.vendor}</dd>
          <dt className="text-slate-500">{t('meters.phases')}</dt><dd>{meter.phases}</dd>
          <dt className="text-slate-500">{t('meters.lastData')}</dt><dd>{formatDateTime(meter.last_minute_at, lang)}</dd>
          <dt className="text-slate-500">{t('meters.connection')}</dt><dd>{meter.direct ? t('meters.direct') : t('meters.viaGateway')}</dd>
        </dl>
        {!meter.direct && (
          <Field label={t('meters.host')} hint={meter.reported_host ? t('meters.hostSeen', { host: meter.reported_host }) : t('meters.hostHint')}>
            {(p) => <Input {...p} value={host} maxLength={70} placeholder="192.168.1.40" className="font-mono"
              pattern="[A-Za-z0-9][A-Za-z0-9.\-]{0,62}(:[0-9]{1,5})?" onChange={(e) => setHost(e.target.value)} />}
          </Field>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-700">{t('meters.thresholds')}</legend>
          <p className="text-xs text-slate-500">
            {th ? t(th.source === 'learned' ? 'meters.learned' : 'meters.manual', { date: formatDateTime(th.at, lang) }) : t('meters.learning')}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('meters.onW')} hint={t('meters.onWHint')}>
              {(p) => <Input {...p} inputMode="decimal" value={form.on_w} placeholder={th ? formatNumber(th.on_w, lang, 1) : ''}
                onChange={(e) => setForm({ ...form, on_w: e.target.value })} />}
            </Field>
            <Field label={t('meters.offW')} hint={t('meters.offWHint')}>
              {(p) => <Input {...p} inputMode="decimal" value={form.off_w} onChange={(e) => setForm({ ...form, off_w: e.target.value })} />}
            </Field>
            <Field label={t('meters.defrostW')} hint={t('meters.defrostWHint')}>
              {(p) => <Input {...p} inputMode="decimal" value={form.defrost_min_w} onChange={(e) => setForm({ ...form, defrost_min_w: e.target.value })} />}
            </Field>
          </div>
          {th && (
            <div>
              <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => run({ thresholds: null })}>{t('meters.relearn')}</Button>
              <span className="text-xs text-slate-500">{t('meters.relearnHint')}</span>
            </div>
          )}
        </fieldset>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" loading={busy}>{t('common.save')}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- a Shelly without a gateway

/** The connection address, shown once, with a copy button and the installation steps. */
export function ShellyAddress({ url }: { url: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <Alert tone="amber" title={t('meters.shelly.once')}>{t('meters.shelly.onceHint')}</Alert>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input readOnly value={url} aria-label={t('meters.shelly.address')} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
        <Button type="button" variant="secondary" onClick={copy}>{copied ? t('meters.shelly.copied') : t('meters.shelly.copy')}</Button>
      </div>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
        <li>{t('meters.shelly.step1')}</li>
        <li>{t('meters.shelly.step2')}</li>
        <li>{t('meters.shelly.step3')}</li>
        <li>{t('meters.shelly.step4')}</li>
      </ol>
      <p className="text-xs text-slate-500">{t('meters.shelly.readOnly')}</p>
    </div>
  );
}

export function DirectShellyDialog({ onClose }: { onClose: () => void }) {
  const { orgId, can } = useCurrentOrg();
  const { t } = useI18n();
  const sites = useSites(orgId);
  const settings = useEnergySettings(orgId);
  const qc = useQueryClient();
  const [form, setForm] = useState({ mac: '', model: 'S3PM-001PCEU16', phases: '1', channels: '1', label: '', site_id: '' });
  const [result, setResult] = useState<{ url: string; deviceId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const f = settings.data?.features;
  const siteId = form.site_id || sites.data?.data[0]?.id || '';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await orgApi(orgId).enrollDirectMeter({ mac: form.mac, model: form.model.trim(), phases: Number(form.phases),
        channels: Number(form.channels), ...(form.label.trim() ? { label: form.label.trim() } : {}), site_id: siteId });
      setResult({ url: r.websocket_url, deviceId: r.device_id });
      await refreshScoped(qc, orgId, ['devices', 'energy', 'probes']);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setError(code === 'meter_already_enrolled' ? t('meters.shelly.already') : code === 'plan_limit_exceeded' ? t('meters.shelly.limit')
        : code === 'feature_not_in_plan' ? t('energy.notInPlanHint') : errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open wide title={t('meters.shelly.title')} onClose={onClose} footer={result ? (
      <>
        <Link to={`/o/${orgId}/devices/${result.deviceId}`} className="self-center text-sm font-medium text-brand-700 hover:underline" onClick={onClose}>
          {t('meters.shelly.openDevice')}
        </Link>
        <Button onClick={onClose}>{t('meters.shelly.done')}</Button>
      </>
    ) : undefined}>
      {result ? <ShellyAddress url={result.url} /> : f && !f.energy ? <p className="text-sm text-slate-600">{t('energy.notInPlanHint')}</p> : (
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <p className="text-sm text-slate-600 sm:col-span-2">{t('meters.shelly.intro')}</p>
          {f && <p className="text-xs text-slate-500 sm:col-span-2">{t('meters.shelly.quota', { used: f.meters_used, max: f.max_meters })}</p>}
          {error && <div className="sm:col-span-2"><Alert tone="red">{error}</Alert></div>}
          <Field label={t('meters.shelly.mac')} hint={t('meters.shelly.macHint')}>
            {(p) => <Input {...p} value={form.mac} required className="font-mono uppercase" placeholder="AABBCCDDEEFF" autoComplete="off"
              onChange={(e) => setForm({ ...form, mac: e.target.value })} />}
          </Field>
          <Field label={t('meters.model')} hint={t('meters.shelly.modelHint')}>
            {(p) => <Input {...p} value={form.model} required maxLength={32} className="font-mono" onChange={(e) => setForm({ ...form, model: e.target.value })} />}
          </Field>
          <Field label={t('meters.phases')}>
            {(p) => (
              <Select {...p} value={form.phases} onChange={(e) => setForm({ ...form, phases: e.target.value })}>
                <option value="1">{t('meters.shelly.single')}</option>
                <option value="3">{t('meters.shelly.three')}</option>
              </Select>
            )}
          </Field>
          <Field label={t('meters.shelly.channels')} hint={t('meters.shelly.channelsHint')}>
            {(p) => <Input {...p} type="number" min={1} max={4} value={form.channels} onChange={(e) => setForm({ ...form, channels: e.target.value })} />}
          </Field>
          <Field label={t('devices.label')}>
            {(p) => <Input {...p} value={form.label} maxLength={100} onChange={(e) => setForm({ ...form, label: e.target.value })} />}
          </Field>
          <Field label={t('devices.site')}>
            {(p) => (
              <Select {...p} value={siteId} required onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
                {sites.data?.data.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            )}
          </Field>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" loading={busy} disabled={!can('admin') || !siteId}>{t('meters.shelly.submit')}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** A new address for a directly connected Shelly: the previous one stops working. */
export function RotateAddressDialog({ deviceId, onClose }: { deviceId: string; onClose: () => void }) {
  const { orgId } = useCurrentOrg();
  const { t } = useI18n();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rotate = async () => {
    setBusy(true);
    setError(null);
    try {
      setUrl((await orgApi(orgId).rotateDirectMeter(deviceId)).websocket_url);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open wide title={t('meters.shelly.newAddress')} onClose={onClose} footer={url ? <Button onClick={onClose}>{t('meters.shelly.done')}</Button> : (
      <>
        <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="danger" onClick={rotate} loading={busy}>{t('meters.shelly.newAddress')}</Button>
      </>
    )}>
      {error && <div className="mb-3"><Alert tone="red">{error}</Alert></div>}
      {url ? <ShellyAddress url={url} /> : <p className="text-sm text-slate-600">{t('meters.shelly.newAddressHint')}</p>}
    </Modal>
  );
}
